import React, { useEffect, useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import AddButton from "@Components/Buttons/AddButton";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import useDeleteItem from "@Hooks/useDeleteItem";
import usePostItem from "@Hooks/usePostItem";
import useGetAll from "@Hooks/useGetAll";
import { Delete } from "@mui/icons-material";
import GenericPopup from "@Components/Popup/Popup";
import { useDispatch } from "react-redux";
import {
  validateData,
  validateField,
  validateUniqueness,
} from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { styled } from "@mui/material/styles";
import {grey} from "@mui/material/colors";
import { setSnackbar } from "@Redux/snackBarSlice";
import axiosInstance from "@API/axios";
import EditIcon from "@mui/icons-material/Edit";
import { UPDATE_USER } from "@API/APIRoutes";

function GenericItemsListing({
  items_URL,
  SAVE_Item,
  DELETE_Item,
  title,
  name = "Item",
  icon,
  attributes,
  validationSchema,
  customFormForAddtion,
  isViewerOnly = false,
  uniqueId = "id",
  clearData,
  listLabel,
  initialColumnVisibilityModel={},
  editable = false,
  filterComponent,
  // Accept edit state from parent
  editMode: parentEditMode,
  setEditMode: parentSetEditMode,
  editItem: parentEditItem,
  setEditItem: parentSetEditItem,
}) {
  const theme = themeCreator();

  const [data, setData] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showAddItemPopup, setShowAddItemPopup] = useState(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [addItemLoading, setAddItemLoading] = useState(false);
  const dispatch = useDispatch();

  const { loading: deleteItemLoading, deleteItem } = useDeleteItem();
  const { loading: PostItemLoading, postItem } = usePostItem();
  const { loading: getAllLoading, getAll } = useGetAll();
  const [errors, setErrors] = useState({});

  // Use parent state if provided, otherwise use local state
  const editMode = parentEditMode !== undefined ? parentEditMode : false;
  const setEditMode = parentSetEditMode || (() => {});
  const editItem = parentEditItem !== undefined ? parentEditItem : null;
  const setEditItem = parentSetEditItem || (() => {});

  // Initialize newItem state based on attributes (excluding the attribute with key "id")
  useEffect(() => {
    const initialNewItem = {};
    attributes.forEach((attr) => {
      if (attr.key !== "id") {
        initialNewItem[attr.key] = "";
      }
    });
    setNewItem(initialNewItem);
  }, [attributes]);

  const closePopup = () => setShowDeletePopup(false);
  const closeAddItemPopup = () => {
    setErrors({});
    setShowAddItemPopup(false);
    setEditMode(false);
    setEditItem(null);
    resetNewItem();
    if (clearData) {
      clearData();
    }
  };

  const openDeletePopup = (itemId) => {
    setIdToBeDeleted(itemId);
    setShowDeletePopup(true);
  };
  useEffect(() => {
    const normalizeListValues = (item, label) => {
      if (!label?.listName || !label?.listAttr) {
        return "";
      }

      const list = item[label.listName] || [];
      return list
        .map((entry) => {
          if (!entry) {
            return "";
          }
          if (typeof entry === "string") {
            return entry.trim().toUpperCase();
          }
          const value =
            (typeof entry === "object" && entry[label.listAttr]) || "";
          return typeof value === "string" ? value.trim().toUpperCase() : "";
        })
        .filter(Boolean)
        .join("\u001F");
    };

    getAll(items_URL, `Failed to fetch ${title}`)
      .then((fetchedData) => {
        const reversedData = [...fetchedData].reverse();

        if (Array.isArray(listLabel) && listLabel.length > 0) {
          const transformedData = reversedData.map((item) => {
            const updatedItem = { ...item };
            listLabel.forEach((label) => {
              updatedItem[label.listName] = normalizeListValues(item, label);
            });
            return updatedItem;
          });
          setData(transformedData);
        } else if (listLabel) {
          const transformedData = reversedData.map((item) => ({
            ...item,
            [listLabel.listName]: normalizeListValues(item, listLabel),
          }));
          setData(transformedData);
        } else {
          setData(reversedData);
        }
      })
      .catch((error) => console.error(error));
  }, []);

  if (getAllLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  const openAddItemPopup = () => {
    setShowAddItemPopup(true);
  };

  const handleTextFieldChange = (attrKey) => (event) => {
    setNewItem((prev) => ({ ...prev, [attrKey]: event.target.value }));
    setErrors({ ...errors, [attrKey]: "" });
  };

  const resetNewItem = () => {
    const emptyObj = {};
    attributes.forEach((attr) => {
      if (attr.key !== "id") {
        emptyObj[attr.key] = "";
      }
    });
    setNewItem(emptyObj);
  };
  const trimData = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        typeof value === "string" ? [key, value.trim()] : [key, value]
      )
    );
  };
  const handleAddItem = () => {
    console.log("ADDD");
    if (customFormForAddtion) {
      customFormForAddtion.formButton().then(async (newUser) => {
        if (newUser) {
          try {
            setAddItemLoading(true);
            const response = await axiosInstance.post(SAVE_Item, newUser);
            if (response.status !== 200) {
              // Store success message in sessionStorage to show after reload
              sessionStorage.setItem('userAddedSuccess', `${name} Added Successfully!`);

              if (postCallBack) postCallBack(); // optional callback if needed
              window.location.reload();
              setAddItemLoading(false);
            }
          } catch (error) {
            console.error("Post failed:", error);

            dispatch(
              setSnackbar({
                open: true,
                message: `Failed to add ${name}`,
                error: true,
              })
            );
            setAddItemLoading(false);
          }
        }
      });
    } else {
      const trimmedItem = trimData(newItem);
      setNewItem(trimmedItem);
      const errors = validateData(trimmedItem, validationSchema);
      setErrors(errors);
      if (Object.keys(errors).length === 0) {
        const isUnique = validateUniqueness(
          data,
          trimmedItem,
          name,
          closeAddItemPopup,
          dispatch
        );
        if (isUnique) {
          postItem(
            SAVE_Item,
            newItem,
            postCallBack,
            `${name} Added Successfully!`,
            `Failed to add ${name}`
          );
        }
      }
    }
  };

  const postCallBack = (addedItem) => {
    setData((prevData) => [
      ...prevData,
      { ...addedItem, id: addedItem[uniqueId] },
    ]);
    resetNewItem();
    closeAddItemPopup();
  };

  const confirmDeleteCallBack = (deletedItemId) => {
    setData((prevData) => prevData.filter((item) => item.id !== deletedItemId));
    closePopup();
  };

  const ConfirmDelete = (itemId) => {
    deleteItem(
      itemId,
      confirmDeleteCallBack,
      DELETE_Item,
      `${name} Deleted Successfully!`,
      `Failed to delete ${name}`
    );
  };

  const openEditPopup = (item) => {
    setEditMode(true);
    setEditItem(item);
    setShowAddItemPopup(true);
    if (clearData) clearData();
  };

  const handleAddOrEditItem = () => {
    if (editMode && customFormForAddtion) {
      customFormForAddtion.formButton(true, editItem).then(async (updatedUser) => {
        if (updatedUser) {
          try {
            setAddItemLoading(true);
            const response = await axiosInstance.put(`${UPDATE_USER}/${editItem.id}`, updatedUser);
            console.log("updatedUser", updatedUser);
            if (response.status === 200) {
              // Update the data in the table instead of reloading
              setData((prevData) =>
                prevData.map((item) => {
                  if (item.id === editItem.id) {
                    // Handle categories and regions properly for the list display
                    let updatedItem = { ...item, ...response.data };

                    // Handle both single listLabel and array of listLabels
                    const labels = Array.isArray(listLabel) ? listLabel : [listLabel];

                    labels.forEach(label => {
                      if (label && label.listName === 'categories' && response.data.categories) {
                        const categoriesString = response.data.categories
                          .map(cat => cat.name?.trim().toUpperCase())
                          .join("\u001F");
                        updatedItem[label.listName] = categoriesString;
                      }

                      if (label && label.listName === 'regions' && response.data.regions) {
                        const regionsString = response.data.regions
                          .map(reg => reg.name?.trim().toUpperCase())
                          .join("\u001F");
                        updatedItem[label.listName] = regionsString;
                      }
                    });

                    return updatedItem;
                  }
                  return item;
                })
              );

              dispatch(
                setSnackbar({
                  open: true,
                  message: `${name} Updated Successfully!`,
                  error: false,
                })
              );
            }
            setAddItemLoading(false);
            closeAddItemPopup();
          } catch (error) {
            dispatch(
              setSnackbar({
                open: true,
                message: `Failed to update ${name}`,
                error: true,
              })
            );
            setAddItemLoading(false);
          }
        }
      });
    } else {
      handleAddItem();
    }
  };

  // Create columns using attributes' key, label, and flex values
  const columns = [
    ...attributes.map((attr) => ({
      field: attr.key,
      headerName: attr.label,
      flex: attr.flex || 2,
      minWidth: 100,
      valueFormatter: (params) => {
        return params?.value === "" ||
          params?.value === null ||
          params?.value === undefined
          ? "N/A"
          : params?.value;
      },
    })),
    ...(listLabel
      ? Array.isArray(listLabel)
        ? listLabel.map(label => ({
            field: label.listName,
            headerName:
              label.listName[0].toUpperCase() + label.listName.slice(1),
            flex: 2.5,
            renderCell: (params) => {
              const rawValue = params.row[label.listName];

              if (!rawValue || rawValue.length === 0) {
                return "N/A";
              }

              // Handle both string and array formats
              let items = [];
              if (typeof rawValue === 'string') {
                items = rawValue.split("\u001F");
              } else if (Array.isArray(rawValue)) {
                items = rawValue.map(item => item.name || item).filter(Boolean);
              } else {
                return "N/A";
              }

              return (
                <TagsContainer>
                  {items.map((item, index) => (
                    <TagItem key={index}>{item}</TagItem>
                  ))}
                </TagsContainer>
              );
            },
          }))
        : [
            {
              field: listLabel.listName,
              headerName:
                listLabel.listName[0].toUpperCase() + listLabel.listName.slice(1),
              flex: 2.5,
              renderCell: (params) => {
                const rawValue = params.row[listLabel.listName];

                if (!rawValue || rawValue.length === 0) {
                  return "N/A";
                }

                // Handle both string and array formats
                let items = [];
                if (typeof rawValue === 'string') {
                  items = rawValue.split("\u001F");
                } else if (Array.isArray(rawValue)) {
                  items = rawValue.map(item => item.name || item).filter(Boolean);
                } else {
                  return "N/A";
                }

                return (
                  <TagsContainer>
                    {items.map((item, index) => (
                      <TagItem key={index}>{item}</TagItem>
                    ))}
                  </TagsContainer>
                );
              },
            },
          ]
      : []),
    ...(!isViewerOnly && (DELETE_Item || editable)
      ? [
          {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            filterable: false,
            width: 120,
            minWidth: 120,
            maxWidth: 120,
            disableReorder: true,
            renderCell: (params) => (
              <ThemeProvider theme={theme}>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {/* Edit Button */}
                  {editable && (
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => openEditPopup(params.row)}
                        size="small"
                      >
                        <EditIcon style={{ color: theme.palette.primary.main }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Delete Button */}
                  {DELETE_Item && (
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => openDeletePopup(params.row.id)}
                        size="small"
                      >
                        <Delete style={{ color: theme.palette.red.main }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ThemeProvider>
            ),
          },
        ]
      : []),
  ];

  const itemsTableProps = {
    data,
    columns,
    columnsEnable: true,
    densityEnable: true,
    disableRowSelectionOnClick: true,
    filtersEnable: true,
    searchBarEnable: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel
  };

  const handleOnBlur = async (field, value) => {
    validateField(
      field,
      value,
      setNewItem,
      newItem,
      validationSchema,
      setErrors
    );
  };

  const addItemPopup = (
    <GenericPopup
      icon={icon}
      open={showAddItemPopup}
      title={editMode ? `Edit ${name}` : `Add ${name}`}
      content={
        <div key={`${editMode}-${editItem?.id || 'new'}`}>
          {customFormForAddtion ? (
            customFormForAddtion.formContent(editMode, editItem)
          ) : (
            <Box noValidate autoComplete="off">
              {attributes
                .filter((attr) => attr.key !== "id")
                .map((attr) => (
                  <Grid item xs={12} key={attr.key}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {attr.label}
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={newItem[attr.key] || ""}
                      onChange={handleTextFieldChange(attr.key)}
                      onBlur={(e) => handleOnBlur(attr.key, e.target.value)}
                      error={!!errors[attr.key]}
                      helperText={errors[attr.key] || ""}
                      placeholder={attr.label}
                    />
                  </Grid>
                ))}
            </Box>
          )}
        </div>
      }
      buttons={[
        {
          text: "Cancel",
          onClick: closeAddItemPopup,
          color: "secondary",
          variant: "outlined",
        },
        {
          text: editMode ? "Update" : "Add",
          onClick: editMode ? handleAddOrEditItem : handleAddItem,
          color: "primary",
          variant: "contained",
          loading: PostItemLoading || addItemLoading,
        },
      ]}
    />
  );
  return (
    <Box>
      <ListingPageTemplate
        title={title}
        PageIcon={icon}
        iconColor={theme.palette.primary.main}
        filterComponent={filterComponent}
        buttons={
          !isViewerOnly
            ? [
                <AddButton
                  key="add-button"
                  onClick={openAddItemPopup}
                  ButtonText={`Add ${name}`}
                />,
              ]
            : []
        }
        tableProps={itemsTableProps}
      />
      {DeletePopup(
        name,
        showDeletePopup,
        closePopup,
        ConfirmDelete,
        idToBeDeleted,
        deleteItemLoading
      )}
      {addItemPopup}
    </Box>
  );
}

export default GenericItemsListing;

export const TagsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  maxWidth: "100%",
  overflowX: "auto",
  whiteSpace: "nowrap",
  borderRadius: theme.spacing(0.5),
  position: "relative",
  "&::-webkit-scrollbar": {
    height: "0", // Thin scrollbar height
  },
}));

// Create a styled component for tagItem
export const TagItem = styled("div")(({ theme }) => ({
  padding: theme.spacing(0.375, 1),
  border: "1.2px solid " + theme.palette.grey.medium,
  borderRadius: theme.spacing(2),
  backgroundColor: grey[300],
}));
